import { Injectable, inject } from '@angular/core';
import { StudentApplicationService } from '@lib/services/student-application/student-application.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { StudentRegistrationActions } from './student-registration.actions';
import { selectStudentRegistrationState } from './student-registration.selectors';

@Injectable()
export class StudentRegistrationEffects {
    private readonly storageKey = 'studentRegistrationState';
    private readonly studentApplicationService = inject(StudentApplicationService);

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
    ) {}

    // Load initial data from API
    loadInitialData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StudentRegistrationActions.loadInitialData),
            switchMap(({ courseId }) => {
                return this.studentApplicationService.getStudentApplicationInitialDetails(courseId).pipe(
                    map((data) => StudentRegistrationActions.loadInitialDataSuccess({ data })),
                    catchError((error) =>
                        of(
                            StudentRegistrationActions.loadInitialDataFailure({
                                error: error.message || 'Failed to load initial data',
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    // Save initial data to session storage when loaded successfully
    saveInitialDataToStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(StudentRegistrationActions.loadInitialDataSuccess),
                withLatestFrom(this.store.select(selectStudentRegistrationState)),
                tap(([_action, state]) => {
                    try {
                        sessionStorage.setItem(this.storageKey, JSON.stringify(state));
                    } catch (error) {
                        console.error('Error saving initial data to sessionStorage:', error);
                    }
                }),
            ),
        { dispatch: false },
    );

    // Save state to sessionStorage whenever it changes
    saveToStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    StudentRegistrationActions.updateBasicDetails,
                    StudentRegistrationActions.updateEducationalDetails,
                    StudentRegistrationActions.updateProfessionalDetails,
                    StudentRegistrationActions.setCurrentStep,
                    StudentRegistrationActions.addCompletedStep,
                    StudentRegistrationActions.removeCompletedStep,
                    StudentRegistrationActions.setSubmitted,
                ),
                withLatestFrom(this.store.select(selectStudentRegistrationState)),
                tap(([_action, state]) => {
                    try {
                        sessionStorage.setItem(this.storageKey, JSON.stringify(state));
                    } catch (error) {
                        console.error('Error saving to sessionStorage:', error);
                    }
                }),
            ),
        { dispatch: false },
    );

    // Load state from sessionStorage on app initialization
    loadFromStorage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StudentRegistrationActions.loadFromStorage),
            switchMap(() => {
                try {
                    const storedState = sessionStorage.getItem(this.storageKey);
                    if (storedState) {
                        const parsedState = JSON.parse(storedState);
                        return of(StudentRegistrationActions.loadFromStorageSuccess({ state: parsedState }));
                    } else {
                        return of(
                            StudentRegistrationActions.loadFromStorageFailure({ error: 'No stored state found' }),
                        );
                    }
                } catch (error) {
                    console.error('Error loading from sessionStorage:', error);
                    return of(
                        StudentRegistrationActions.loadFromStorageFailure({ error: 'Error loading from storage' }),
                    );
                }
            }),
        ),
    );

    // Clear storage when resetting state, submitting, or canceling
    clearStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    StudentRegistrationActions.resetState,
                    StudentRegistrationActions.resetFormData,
                    StudentRegistrationActions.submitApplicationSuccess,
                    StudentRegistrationActions.cancelRegistration,
                ),
                tap(() => {
                    try {
                        sessionStorage.removeItem(this.storageKey);
                    } catch (error) {
                        console.error('Error clearing sessionStorage:', error);
                    }
                }),
            ),
        { dispatch: false },
    );

    // Submit application effect (placeholder for API integration)
    submitApplication$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StudentRegistrationActions.submitApplication),
            withLatestFrom(this.store.select(selectStudentRegistrationState)),
            switchMap(([_action, state]) => {
                return of({ success: true, data: state }).pipe(
                    map((response) => StudentRegistrationActions.submitApplicationSuccess({ response })),
                    catchError((error) =>
                        of(StudentRegistrationActions.submitApplicationFailure({ error: error.message })),
                    ),
                );
            }),
        ),
    );

    // Auto-save effect for form changes with debouncing
    autoSave$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    StudentRegistrationActions.updateBasicDetails,
                    StudentRegistrationActions.updateEducationalDetails,
                    StudentRegistrationActions.updateProfessionalDetails,
                ),
                withLatestFrom(this.store.select(selectStudentRegistrationState)),
                tap(([_action, state]) => {
                    // Auto-save to sessionStorage with debouncing
                    setTimeout(() => {
                        try {
                            sessionStorage.setItem(this.storageKey, JSON.stringify(state));
                        } catch (error) {
                            console.error('Error auto-saving to sessionStorage:', error);
                        }
                    }, 1000); // 1 second debounce
                }),
            ),
        { dispatch: false },
    );
}
